import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Mail, Phone, MapPin, MessageCircle, CheckCircle, Send, AlertCircle } from 'lucide-react';
import { useScrollAnimation, fadeInLeft, fadeInRight, staggerContainer } from '../../hooks/useScrollAnimation';
import SectionTitle from '../ui/SectionTitle';
import { useLang } from '../context/LanguageContext';
import { EMAIL, WHATSAPP_URL, PHONE_MAIN, PHONE_ALT } from '../../data/contact';

const linkClass = 'text-gray-600 hover:text-primary underline-offset-4 hover:underline transition-colors';

// Etiqueta oculta a la vista (el placeholder ya la muestra) pero leída por lectores de pantalla,
// y el mensaje de error enlazado al campo con aria-describedby.
const Field = ({ id, label, error, children }) => {
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="sr-only">{label}</label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : undefined,
      })}
      {error && <p id={errorId} className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  );
};

const ContactSection = ({ id }) => {
  const { ref, inView } = useScrollAnimation();
  const { t } = useLang();
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      // Solo es éxito si responde la función con { ok: true } (no una página HTML con 200).
      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.ok !== true) throw new Error(`HTTP ${res.status}`);
      setStatus('sent');
      reset();
      setTimeout(() => setStatus('idle'), 6000);
    } catch {
      setStatus('error');
    }
  };

  const sending = status === 'sending';

  const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-dark text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all";
  const errorText = (field) => {
    const error = errors[field];
    if (!error) return null;
    if (error.type === 'pattern') return t.contact.errors.email;
    if (error.type === 'minLength') return t.contact.errors.messageMin;
    return t.contact.errors.required;
  };

  return (
    <section id={id} className="section-padding bg-cream relative overflow-hidden" ref={ref}>
      <img
        src="/images/fondos/hoja.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      <div className="absolute inset-0 bg-cream/85 pointer-events-none" />
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="container-max relative z-10"
      >
        <SectionTitle badge={t.contact.badge} title={t.contact.title} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Contact info */}
          <motion.div variants={fadeInLeft}>
            <h3 className="font-display text-2xl font-bold text-primary mb-8">
              {t.contact.info.title}
            </h3>
            <div className="space-y-6 mb-10">
              {[
                { icon: Mail, content: <a href={`mailto:${EMAIL}`} className={linkClass}>{EMAIL}</a> },
                {
                  icon: Phone,
                  content: (
                    <span className="text-gray-600">
                      (506){' '}
                      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>{PHONE_MAIN}</a>
                      {' / '}
                      <a href={PHONE_ALT.href} className={linkClass}>{PHONE_ALT.label}</a>
                    </span>
                  ),
                },
                { icon: MapPin, content: <span className="text-gray-600">{t.contact.info.location}</span> },
              ].map(({ icon: Icon, content }, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={22} className="text-primary" aria-hidden="true" />
                  </div>
                  {content}
                </div>
              ))}
            </div>

            {/* WhatsApp button */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-all"
            >
              <MessageCircle size={20} aria-hidden="true" />
              {t.footer.whatsapp}
            </a>

          </motion.div>

          {/* Right: Form */}
          <motion.div variants={fadeInRight}>
            {status === 'sent' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                role="status"
                className="bg-primary/10 rounded-2xl p-12 text-center h-full flex flex-col items-center justify-center"
              >
                <CheckCircle size={48} className="text-primary mb-4" aria-hidden="true" />
                <h3 className="font-display text-2xl font-bold text-primary mb-2">
                  {t.contact.successTitle}
                </h3>
                <p className="text-gray-600">{t.contact.successMessage}</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <Field id="contact-name" label={t.contact.namePlaceholder} error={errorText('name')}>
                  {(aria) => (
                    <input
                      {...register('name', { required: true })}
                      {...aria}
                      autoComplete="name"
                      placeholder={t.contact.namePlaceholder}
                      className={inputClass}
                    />
                  )}
                </Field>

                <Field id="contact-company" label={t.contact.companyPlaceholder} error={errorText('company')}>
                  {(aria) => (
                    <input
                      {...register('company', { required: true })}
                      {...aria}
                      autoComplete="organization"
                      placeholder={t.contact.companyPlaceholder}
                      className={inputClass}
                    />
                  )}
                </Field>

                <Field id="contact-email" label={t.contact.emailPlaceholder} error={errorText('email')}>
                  {(aria) => (
                    <input
                      {...register('email', {
                        required: true,
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      })}
                      {...aria}
                      type="email"
                      autoComplete="email"
                      placeholder={t.contact.emailPlaceholder}
                      className={inputClass}
                    />
                  )}
                </Field>

                <Field id="contact-phone" label={t.contact.phonePlaceholder}>
                  {(aria) => (
                    <input
                      {...register('phone')}
                      {...aria}
                      type="tel"
                      autoComplete="tel"
                      placeholder={t.contact.phonePlaceholder}
                      className={inputClass}
                    />
                  )}
                </Field>

                <Field id="contact-message" label={t.contact.messagePlaceholder} error={errorText('message')}>
                  {(aria) => (
                    <textarea
                      {...register('message', { required: true, minLength: 20 })}
                      {...aria}
                      placeholder={t.contact.messagePlaceholder}
                      rows={4}
                      className={inputClass}
                    />
                  )}
                </Field>

                {/* Campo trampa anti-spam: invisible para personas, los robots suelen llenarlo */}
                <input
                  {...register('website')}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] w-px h-px opacity-0"
                />

                {status === 'error' && (
                  <div role="alert" className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4 text-sm">
                    <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-red-700">
                      <span className="font-semibold">{t.contact.errorTitle}</span>{' '}
                      {t.contact.errorMessage}{' '}
                      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="underline font-medium">
                        WhatsApp
                      </a>
                      .
                    </p>
                  </div>
                )}

                {/* Consent */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('consent')}
                    className="mt-1 w-4 h-4 accent-primary"
                  />
                  <span className="text-sm text-gray-600">{t.contact.consent}</span>
                </label>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-secondary transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                      />
                      {t.contact.sending}
                    </>
                  ) : (
                    <>
                      <Send size={18} aria-hidden="true" />
                      {t.contact.submit}
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default ContactSection;
